import React, { useEffect, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { MainContent } from '../styles/DashboardStyles';
import { usePaymentViewModel } from '../viewmodels/PaymentViewModel';
import PaymentStatsCards from '../components/PaymentStatsCards';
import PaymentSearchBar from '../components/PaymentSearchBar';
import PaymentListTable from '../components/PaymentListTable';
import PageHeader from '../components/ui/PageHeader';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { colors, typography, spacing, borderRadius, zIndex } from '../styles/designTokens';

// --- 연월 필터 관련 스타일 수정 ---
const FilterLabel = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  margin-right: ${spacing[2]};
  font-weight: ${typography.fontWeight.semibold};
`;

const MonthControlContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};

  .month-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }
  
  .calendar-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: ${colors.semantic.text.tertiary};
    z-index: ${zIndex.docked};
  }
`;

const MonthInput = styled.input`
  width: 150px;
  padding: ${spacing[2]} ${spacing[2]} ${spacing[2]} 34px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.secondary};
  background: ${colors.semantic.background.primary};
  transition: all 0.2s;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  font-family: ${typography.fontFamily.sans};

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    cursor: pointer;
  }
  
  &:focus {
    outline: none;
    border-color: ${colors.primary[500]};
    box-shadow: 0 0 0 1px ${colors.primary[500]};
  }
`;

const MonthNavButton = styled.button`
  padding: ${spacing[2]};
  background: transparent;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${borderRadius.md};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.secondary[600]};
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background: ${colors.neutral[100]};
    border-color: ${colors.secondary[400]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${colors.neutral[200]};
    border-color: ${colors.neutral[200]};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;
// --- 연월 필터 관련 스타일 끝 ---



// 에러 알림 스타일
const ErrorAlert = styled.div`
  display: flex;
  align-items: center;
  padding: ${spacing[4]} ${spacing[5]};
  background: ${colors.warning[100]};
  border: 1px solid ${colors.warning[300]};
  border-radius: ${borderRadius.lg};
  color: ${colors.warning[800]};
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing[6]};
  
  .icon {
    margin-right: ${spacing[3]};
    font-size: ${typography.fontSize.base};
  }
  
  .close-btn {
    margin-left: auto;
    background: none;
    border: none;
    color: ${colors.warning[800]};
    cursor: pointer;
    font-size: ${typography.fontSize.lg};
    padding: 0;
    
    &:hover {
      opacity: 0.7;
    }

    &:focus-visible {
      outline: 2px solid ${colors.warning[600]};
      outline-offset: 2px;
      border-radius: ${borderRadius.sm};
    }
  }
`;

// 결제 관리 페이지 컴포넌트
const PaymentsPage: React.FC = () => {
  // ViewModel 사용
  const {
    // 상태
    payments,
    loading,
    error,
    currentPage,
    totalPages,
    total,
    limit,
    searchQuery: vmSearchQuery,
    yearMonth: vmYearMonth,
    sortBy,
    order,
    stats,
    statsLoading,
    statsError,

    // 액션
    fetchPaymentsAndStats,
    setSearchQuery: vmSetSearchQuery,
    setYearMonth: vmSetYearMonth,
    setSorting,
    setCurrentPage,
    setLimit,
    clearError,
    clearStatsError
  } = usePaymentViewModel();

  // PaymentsPage 내부 상태로 사용자가 UI에서 선택한 값을 관리 (Debounce 등에 활용 가능)
  const [localSearchQuery, setLocalSearchQuery] = useState(vmSearchQuery);
  const [localYearMonth, setLocalYearMonth] = useState(vmYearMonth);

  const isInitialMount = useRef(true);

  // ViewModel의 상태가 변경되면 local 상태도 동기화 (ViewModel -> local)
  useEffect(() => {
    if (isInitialMount.current || vmSearchQuery !== localSearchQuery) {
      setLocalSearchQuery(vmSearchQuery);
    }
  }, [vmSearchQuery, localSearchQuery]);

  useEffect(() => {
    if (isInitialMount.current || vmYearMonth !== localYearMonth) {
      setLocalYearMonth(vmYearMonth);
    }
  }, [vmYearMonth, localYearMonth]);

  // 데이터 로드 로직: 초기 마운트 시, 또는 ViewModel의 필터 값(vmYearMonth, vmSearchQuery)이 변경될 때 실행
  useEffect(() => {
    console.log('PaymentsPage: useEffect[fetchPaymentsAndStats, vmYearMonth, vmSearchQuery] 실행');
    console.log('현재 vmYearMonth:', vmYearMonth, '현재 vmSearchQuery:', vmSearchQuery);

    if (isInitialMount.current) {
      console.log('초기 마운트, API 호출');
      fetchPaymentsAndStats({ q: vmSearchQuery, year_month: vmYearMonth, page: 1, limit: limit, sort_by: sortBy, order: order });
      isInitialMount.current = false;
    } else {
      console.log('vmYearMonth 또는 vmSearchQuery 변경 감지, API 호출');
      fetchPaymentsAndStats({ q: vmSearchQuery, year_month: vmYearMonth, page: 1 });
    }
  }, [vmYearMonth, vmSearchQuery, fetchPaymentsAndStats, limit, sortBy, order]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchPaymentsAndStats({ page, q: localSearchQuery, year_month: localYearMonth, limit, sort_by: sortBy, order });
  };

  // 페이지 크기 변경 핸들러
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    fetchPaymentsAndStats({ limit: newLimit, page: 1, q: localSearchQuery, year_month: localYearMonth, sort_by: sortBy, order });
  };

  // 검색 핸들러
  const handleSearchSubmit = useCallback(() => {
    console.log('🔍 PaymentsPage - 검색 실행 버튼 클릭. local 상태:', {
      searchQuery: localSearchQuery,
      yearMonth: localYearMonth
    });
    vmSetSearchQuery(localSearchQuery);
    vmSetYearMonth(localYearMonth);
  }, [localSearchQuery, localYearMonth, vmSetSearchQuery, vmSetYearMonth]);

  // 정렬 변경 핸들러
  const handleSortChange = (newSortBy: string, newOrder: 'asc' | 'desc') => {
    setSorting(newSortBy, newOrder);
  };

  // 연월 변경 직접 핸들러 (MonthInput의 onChange)
  const handleDirectYearMonthChange = (newYearMonth: string) => {
    console.log('📅 PaymentsPage - 연월 직접 변경 (Input -> PaymentsPage local). 새 값:', newYearMonth);
    setLocalYearMonth(newYearMonth);
    vmSetYearMonth(newYearMonth); // ViewModel 상태 업데이트 -> useEffect 트리거
  };

  // 월 이동 버튼 핸들러 (PaymentSearchBar에서 가져옴)
  const handleMonthNavigate = (direction: 'prev' | 'next') => {
    const currentDate = new Date(localYearMonth + '-01T00:00:00Z'); // UTC로 해석 보장
    if (direction === 'prev') {
      currentDate.setUTCMonth(currentDate.getUTCMonth() - 1);
    } else {
      currentDate.setUTCMonth(currentDate.getUTCMonth() + 1);
    }
    const newYearMonth = currentDate.toISOString().slice(0, 7);
    setLocalYearMonth(newYearMonth); // local 상태 업데이트
    vmSetYearMonth(newYearMonth);    // ViewModel 상태 업데이트 -> useEffect 트리거
    console.log('📅 PaymentsPage - 연월 이동 버튼 클릭. 새 값:', newYearMonth);
  };

  // 검색어 변경 핸들러
  const handleSearchQueryChange = (query: string) => {
    console.log('📝 PaymentsPage - 검색어 변경 (SearchBar -> PaymentsPage local). 새 값:', query);
    setLocalSearchQuery(query);
  };

  // 필터 초기화 핸들러
  const handleResetFilters = () => {
    console.log('🔄 PaymentsPage - 필터 초기화 버튼 클릭');
    const defaultYearMonth = new Date().toISOString().slice(0, 7);
    const defaultSearchQuery = '';

    setLocalSearchQuery(defaultSearchQuery);
    setLocalYearMonth(defaultYearMonth);

    // ViewModel 상태도 기본값으로 설정 (useEffect 트리거)
    vmSetSearchQuery(defaultSearchQuery);
    vmSetYearMonth(defaultYearMonth);

    // ViewModel의 필터 초기화 함수는 직접 사용하지 않음.
    // vmSetSearchQuery와 vmSetYearMonth 호출이 useEffect를 트리거하여
    // fetchPaymentsAndStats가 기본값으로 호출되도록 함.
  };

  return (
    <MainContent>
      {/* 페이지 헤더 */}
      <PageHeader
        title="결제 관리"
        description="결제 내역 조회 및 통계 관리"
      >
        <MonthControlContainer>
          <FilterLabel>조회 기준월:</FilterLabel>
          <MonthNavButton
            onClick={() => handleMonthNavigate('prev')}
            disabled={loading}
            aria-label="이전 달"
          >
            <ChevronLeft size={18} />
          </MonthNavButton>
          <div className="month-input-wrapper">
            <Calendar size={16} className="calendar-icon" />
            <MonthInput
              id="pageHeaderYearMonth"
              type="month"
              value={localYearMonth}
              onChange={(e) => handleDirectYearMonthChange(e.target.value)}
              disabled={loading}
            />
          </div>
          <MonthNavButton
            onClick={() => handleMonthNavigate('next')}
            disabled={loading}
            aria-label="다음 달"
          >
            <ChevronRight size={18} />
          </MonthNavButton>
        </MonthControlContainer>
      </PageHeader>

      {/* 에러 메시지 */}
      {error && (
        <ErrorAlert>
          <span className="icon">⚠️</span>
          {error}
          <button
            className="close-btn"
            onClick={clearError}
            aria-label="닫기"
          >
            ×
          </button>
        </ErrorAlert>
      )}

      {/* 통계 에러 메시지 */}
      {statsError && (
        <ErrorAlert>
          <span className="icon">⚠️</span>
          {statsError}
          <button
            className="close-btn"
            onClick={clearStatsError}
            aria-label="닫기"
          >
            ×
          </button>
        </ErrorAlert>
      )}

      {/* 통계 카드 */}
      <PaymentStatsCards
        stats={stats}
        loading={statsLoading}
        error={statsError}
        yearMonth={vmYearMonth}
      />

      {/* 검색 및 필터 바 */}
      <PaymentSearchBar
        searchQuery={localSearchQuery}
        sortBy={sortBy}
        order={order}
        onSearchChange={handleSearchQueryChange}
        onSortChange={handleSortChange}
        onSearch={handleSearchSubmit}
        onReset={handleResetFilters}
        loading={loading}
      />

      {/* 결제 목록 테이블 */}
      <PaymentListTable
        payments={payments}
        loading={loading}
        error={error}
        currentPage={currentPage}
        totalPages={totalPages}
        total={total}
        limit={limit}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
      />
    </MainContent>
  );
};

export default PaymentsPage; 